import React, {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useState,
  useMemo,
  useContext,
} from 'react';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  SideDrawerProps,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { CollapsibleSection } from 'components/CollapsibleSection';
import { Field, Form } from 'react-final-form';
import RadioGroupField from 'components/RadioGroupField';
import InputField from 'components/InputField';
import { Button } from 'ncoded-component-library';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import PhoneFieldWrapper from 'components/PhoneFieldWrapper';
import { getInitialPhone } from 'phoneNumberUtils';
import XcircleIcon from 'icons/Xcircle.icon';
import DealContext from '../../providers/Deal/Deal.context';
import { Colleague, TransferResponse } from 'models/DealRoom';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import { ObjectValues } from 'types';
import useTransferDeal from '../../hooks/useTransferDeal';
import EmailField from 'components/EmailField';

import './TransferListingDrawer.styles.scss';
import './TransferListingDrawer.styles.responsive.scss';

type TransferListingDrawerProps = {
  className?: string;
  selectedColleague?: Colleague;
} & SideDrawerProps;

type BannerProps = {
  title: string;
  text?: string;
  icon: React.ReactNode;
  className: string;
  buttonText?: string;
  onButton?: () => void;
};

const CHECK_OUTCOME = {
  NOT_EXISTS: 'not-exist',
  EXISTS: 'exists',
  CANNOT_BE_OWNER: 'cantBeOwner',
} as const;

type CheckOutcome = ObjectValues<typeof CHECK_OUTCOME>;

const BANNERS = {
  [CHECK_OUTCOME.NOT_EXISTS]: {
    title: 'userDontExist',
    icon: <XcircleIcon />,
    className: 'not-exists',
  },
  [CHECK_OUTCOME.EXISTS]: {
    title: 'userExists',
    icon: <CheckcircleIcon />,
    className: 'user-exists',
  },
  [CHECK_OUTCOME.CANNOT_BE_OWNER]: {
    title: 'userNotAbleToBe',
    text: 'userNotAbleToBeMess',
    icon: <XcircleIcon />,
    className: 'not-exists',
  },
} as const;

const BannerForTranser: React.FC<BannerProps> = ({
  text,
  title,
  icon,
  className,
  buttonText,
  onButton,
}) => {
  const classes = classNames(className, 'transfer-banner');
  const { t } = useTranslation();

  return (
    <div className={classes}>
      {icon}
      <div>
        <p>{t(title)}</p>
        {text && <p>{t(text)}</p>}
      </div>
      {buttonText && (
        <Button onClick={() => onButton?.()}>{t(buttonText)}</Button>
      )}
    </div>
  );
};

const TransferListingDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  TransferListingDrawerProps
> = (props, ref) => {
  const { className, selectedColleague, ...rest } = props;

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    dealRoom: { _id: dealRoomId, listingId },
    colleagues,
  } = useContext(DealContext);

  const { updateProvidersAfterDealTransfer } = useTransferDeal();

  const [mail, setMail] = useState('');
  const [checkOutcome, setCheckOutcome] = useState<CheckOutcome>(null);
  const [resetDrawer, setResetDrawer] = useState(0);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const colleaguesOptions = useMemo(
    () =>
      colleagues
        .filter(({ isOwner }) => !isOwner)
        .map(({ _id, name, email }) => ({
          id: _id,
          value: email,
          label: name || email,
        })),
    [colleagues],
  );

  const initialColleaguesValues = useMemo(
    () => (selectedColleague ? { colleagueMail: selectedColleague.email } : {}),
    [selectedColleague],
  );

  const classes = classNames('transfer-listing-drawer', className);

  const isNotTranserable =
    !checkOutcome || checkOutcome === CHECK_OUTCOME.CANNOT_BE_OWNER;

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();

  const resetState = useCallback(() => {
    setTimeout(() => {
      setCheckOutcome(null);
      setMail('');
      setResetDrawer((prev) => prev + 1);
    }, 1000);
  }, []);

  const onSuccessfullyTransfered = useCallback(
    (transferResponse: TransferResponse) => {
      updateProvidersAfterDealTransfer(transferResponse);
      popSuccess(t('listingTransfered'));
      drawer.setOpen(false);
    },
    [updateProvidersAfterDealTransfer, popSuccess, t, drawer],
  );

  const transferToColleague = useCallback(
    async ({ colleagueMail }) => {
      try {
        const { data } = await api.portfolio.admin.transferToColleague(
          listingId,
          colleagueMail,
          dealRoomId,
        );

        onSuccessfullyTransfered(data);
      } catch (e) {
        popServerError(e);
      }
    },
    [listingId, dealRoomId, onSuccessfullyTransfered, popServerError],
  );

  const transferToNewUser = useCallback(
    async (user) => {
      try {
        const { data } = await api.portfolio.admin.transferToNewUser(
          listingId,
          user,
          dealRoomId,
        );

        onSuccessfullyTransfered(data);
      } catch (e) {
        popServerError(e);
      }
    },
    [listingId, dealRoomId, onSuccessfullyTransfered, popServerError],
  );

  const checkExistance = useCallback(
    async ({ email }) => {
      try {
        const {
          data: { isBrokerExist, canBeListingOwner },
        } = await api.portfolio.admin.checkTransferEmail(email, dealRoomId);

        if (!isBrokerExist) {
          setCheckOutcome(CHECK_OUTCOME.NOT_EXISTS);
          return;
        }

        if (!canBeListingOwner) {
          setCheckOutcome(CHECK_OUTCOME.CANNOT_BE_OWNER);
          return;
        }

        setMail(email);
        setCheckOutcome(CHECK_OUTCOME.EXISTS);
      } catch (e) {
        popServerError(e);
      }
    },
    [dealRoomId, popServerError],
  );

  const UserForm = useMemo(() => {
    const doesUserExist = checkOutcome === CHECK_OUTCOME.EXISTS;
    const btnTxt = doesUserExist ? 'transferToUser' : 'inviteAndTransfer';

    const initialValues = doesUserExist
      ? {
          email: mail,
        }
      : {
          email: mail,
          phone: getInitialPhone(),
        };

    const formContent = doesUserExist ? (
      <Form
        onSubmit={transferToNewUser}
        initialValues={initialValues}
        render={({ handleSubmit, invalid, submitting }) => (
          <form onSubmit={handleSubmit}>
            <EmailField readOnly required />

            <Button type="submit" disabled={invalid || submitting}>
              {t(btnTxt)}
            </Button>
          </form>
        )}
      />
    ) : (
      <Form
        onSubmit={transferToNewUser}
        initialValues={initialValues}
        render={({ handleSubmit, invalid, submitting }) => (
          <form onSubmit={handleSubmit}>
            <EmailField required />
            <Field
              name="firstName"
              label={t('firstName')}
              component={InputField}
            />
            <Field
              name="lastName"
              label={t('lastName')}
              component={InputField}
            />
            <PhoneFieldWrapper />
            <Button type="submit" disabled={invalid || submitting}>
              {t(btnTxt)}
            </Button>
          </form>
        )}
      />
    );

    return formContent;
  }, [checkOutcome, mail, transferToNewUser, t]);

  const drawerContent = useMemo(
    () => (
      <>
        <CollapsibleSection
          className="white-panel"
          defaultOpen={!!selectedColleague}
          trigger={
            <p className="transfer-listing-drawer__collapse__heading">
              {t('makeAColleagueAdmin')}
            </p>
          }
        >
          {colleaguesOptions.length ? (
            <Form
              onSubmit={transferToColleague}
              initialValues={initialColleaguesValues}
              render={({ handleSubmit, invalid, submitting }) => (
                <form onSubmit={handleSubmit}>
                  <RadioGroupField
                    name="colleagueMail"
                    options={colleaguesOptions}
                  />
                  <Button type="submit" disabled={invalid || submitting}>
                    {t('tranferToColleague')}
                  </Button>
                </form>
              )}
            />
          ) : (
            <div>{t('noColleaguesAvailableForTransfer')}</div>
          )}
        </CollapsibleSection>
        <CollapsibleSection
          className="white-panel"
          trigger={
            <p className="transfer-listing-drawer__collapse__heading">
              {t('transferToNewUser')}
            </p>
          }
        >
          {checkOutcome && (
            <BannerForTranser
              {...BANNERS[checkOutcome]}
              onButton={async () => {
                try {
                  await api.portfolio.admin.remindUserVerify(listingId, mail);

                  popSuccess(t('successInvitedColleague'));
                } catch (e) {
                  popServerError(e);
                }
              }}
            />
          )}

          {isNotTranserable ? (
            <Form
              onSubmit={checkExistance}
              initialValues={{
                email: mail,
              }}
              render={({ handleSubmit, invalid, submitting }) => (
                <form onSubmit={handleSubmit}>
                  <EmailField required onChangeHandle={setMail} />
                  <Button type="submit" disabled={invalid || submitting}>
                    {t('findUserOnResolve')}
                  </Button>
                </form>
              )}
            />
          ) : (
            UserForm
          )}
        </CollapsibleSection>
      </>
    ),
    [
      UserForm,
      checkOutcome,
      colleaguesOptions,
      initialColleaguesValues,
      isNotTranserable,
      listingId,
      mail,
      selectedColleague,
      popServerError,
      popSuccess,
      t,
      transferToColleague,
      checkExistance,
    ],
  );

  return (
    <SideDrawer
      key={resetDrawer}
      ref={drawerRef}
      className={classes}
      closeAction={resetState}
      {...rest}
    >
      <p>{t('transferListingDrawerTitle')}</p>
      <p>{t('transferListingDrawerMessage')}</p>

      {drawer?.open && drawerContent}
    </SideDrawer>
  );
};

export default forwardRef(TransferListingDrawer);
